import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';

import PageWrapper from '../../../page-wrapper';
import Button from '../../../button';
import BlockBuilder from '../../BlockBuilder';
import sharedStyles from '../../../styles';
import breakpoints from '../../../breakpoints';

const InnerContainer = styled.div`
  display: flex;
  max-width: ${breakpoints.xl};
  margin-block: ${sharedStyles.spacing['spacing-2xl']};
  flex-flow: column-reverse;
  gap: ${sharedStyles.spacing['spacing-lg']};

  @media screen and (min-width: ${breakpoints.lg}) {
    margin-block: ${sharedStyles.spacing['spacing-3xl']};
    align-items: center;
    gap: ${sharedStyles.spacing['spacing-1xl']};
    flex-direction: ${({ layout }) =>
      layout === 'right' ? 'row' : 'row-reverse'};
  }
`;

const InnerSection = styled.div`
  display: block;
  padding: 0;

  p {
    font-size: ${sharedStyles.fontSize.lg};
  }

  .btn-primary {
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
      width: auto;
    }
  }

  @media screen and (min-width: ${breakpoints.lg}) {
    width: 50%;
  }

  &.image-section {
    padding: 0;
    margin: 0;
    align-self: stretch;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

function BlockBuilderFeatureRow({ value, getInternalLink }) {
  let imageWidth = value?.media?.asset?.metadata?.dimensions?.width;

  const getBreakpoint = breakpoint => breakpoint.replace('px', '');

  if (typeof window !== 'undefined' && imageWidth) {
    const screenWidth = window?.innerWidth;

    if (screenWidth < imageWidth) {
      if (screenWidth < getBreakpoint(breakpoints.xs)) {
        imageWidth = 360;
      } else if (screenWidth < getBreakpoint(breakpoints.sm)) {
        imageWidth = 588;
      } else if (screenWidth < getBreakpoint(breakpoints.md)) {
        imageWidth = 716;
      } else if (screenWidth < getBreakpoint(breakpoints.lg)) {
        imageWidth = 972;
      } else {
        imageWidth = 593;
      }
    }
  }

  return (
    <PageWrapper fullWidth background="transparent" padding="0">
      <InnerContainer layout={value.desktopLayout}>
        <InnerSection>
          <BlockBuilder
            blocks={[value.copy]}
            getInternalLink={getInternalLink}
          />
          {value?.cta?.caption && (
            <>
              {value?.cta?.linkType === 'internal' && (
                <Button
                  to={getInternalLink({
                    ...value.cta,
                    reference: value.cta?.internalLink,
                  })}
                >
                  {value.cta.caption}
                </Button>
              )}
              {value?.cta?.linkType === 'external' && (
                <Button
                  href={value?.cta?.href}
                  target={value?.cta?.blank ? '_blank' : '_self'}
                  rel="noreferrer"
                >
                  {value.cta.caption}
                </Button>
              )}
            </>
          )}
        </InnerSection>
        <InnerSection className="image-section">
          {value?.media?.asset && (
            <Wrapper>
              <SanityImage
                asset={value?.media?.asset}
                hotspot={value?.media?.hotspot}
                crop={value?.media?.crop}
                alt={value?.media?.alt || ''}
                width={parseInt(imageWidth, 10)}
                options={{ __experimentalAspectRatio: true }}
                sizes={`(max-width: ${breakpoints.xs}) 360px,
                        (max-width: ${breakpoints.sm}) 588px,
                        (max-width: ${breakpoints.md}) 716px,
                        (max-width: ${breakpoints.lg}) 972px,
                        593px`}
              />
            </Wrapper>
          )}
        </InnerSection>
      </InnerContainer>
    </PageWrapper>
  );
}

BlockBuilderFeatureRow.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    media: PropTypes.shape({
      asset: PropTypes.shape({
        metadata: PropTypes.shape({
          dimensions: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
      }),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
      alt: PropTypes.string,
    }),
    copy: PropTypes.shape({}),
    desktopLayout: PropTypes.string,
    cta: PropTypes.shape({
      caption: PropTypes.string,
      linkType: PropTypes.string,
      internalLink: PropTypes.shape({}),
      href: PropTypes.string,
      blank: PropTypes.bool,
    }),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderFeatureRow;
